import { useState, useCallback, useEffect, useRef } from 'react'
import { CampaignFilterContext } from '../context/CampaignFilter.context'
import {
   ICampaignFilterConfig,
   ICampaignFilterContext,
   ICampaignFilterProviderProps,
   IFilterCriteria,
} from '../interfaces'
import { ConfigurationSteps } from './ConfigurationSteps'

const DEFAULT_CONFIGURATION: ICampaignFilterConfig = {
   campaign_filters: {
      eligible_criteria: [],
   },
   first_to_contact: {
      buffer_time: 0,
   },
}

export const CampaignFilter = ({
   initialConfiguration,
   onSave,
   onNextStep,
   onPreviousStep,
   showNavigation = true,
   submitLabel = 'Apply Filters',
   children,
}: ICampaignFilterProviderProps) => {
   const [configuration, setConfiguration] = useState<ICampaignFilterConfig>(
      initialConfiguration ?? DEFAULT_CONFIGURATION
   )
   const [step, setStep] = useState<number>(1)

   const isSyncingFromProps = useRef(false)

   useEffect(() => {
      if (initialConfiguration) {
         isSyncingFromProps.current = true
         setConfiguration(initialConfiguration)
      }
   }, [initialConfiguration])

   const updateConfiguration = useCallback(
      (partial: Partial<ICampaignFilterConfig>) => {
         setConfiguration((prev) => ({
            ...prev,
            ...partial,
         }))
      },
      []
   )

   const getFirstFilterCriteria = useCallback((): IFilterCriteria | null => {
      const criteria =
         configuration?.campaign_filters?.eligible_criteria?.[0]
      if (!criteria) return null
      return {
         ...criteria,
         value: Array.isArray(criteria.value)
            ? criteria.value.map(String)
            : [],
      }
   }, [configuration])

   const getRestFilterCriterias = useCallback((): IFilterCriteria[] => {
      const criterias =
         configuration?.campaign_filters?.eligible_criteria?.slice(1)
      if (!criterias) return []
      return criterias.map((criteria) => ({
         ...criteria,
         value: Array.isArray(criteria.value)
            ? criteria.value.map(String)
            : [],
      }))
   }, [configuration])

   const setFirstFilterCriteria = useCallback(
      (criteria: IFilterCriteria) => {
         setConfiguration((prev) => ({
            ...prev,
            campaign_filters: {
               ...prev.campaign_filters,
               eligible_criteria: [
                  {
                     field: criteria.field,
                     value: criteria.value,
                     logic: criteria.logic,
                     operator: criteria.operator,
                  },
                  ...(prev.campaign_filters?.eligible_criteria?.slice(1) ?? []),
               ],
            },
         }))
      },
      []
   )

   const value: ICampaignFilterContext = {
      configuration,
      step,
      getFirstFilterCriteria,
      getRestFilterCriterias,
      setFirstFilterCriteria,
      updateConfiguration,
      setStep,
      onSave,
      onNextStep,
      onPreviousStep,
      showNavigation,
      submitLabel,
   }

   return (
      <CampaignFilterContext.Provider value={value}>
         {children ?? <ConfigurationSteps />}
      </CampaignFilterContext.Provider>
   )
}
